import {
  datesToISO,
  formHeaders,
  importDataUrlMapOptions,
  projectSearchValuesPayload,
  success,
  swal,
} from "@";
import api, { baseURL } from "./axios";
import { useAuthStore } from "@/shared/stores/useAuthStore.js";

// Log out the user by removing user data from local storage and redirecting to login page
export const logOut = () => {
  localStorage.removeItem("user");
  window.location.replace("/login");
};

// Fetch projects based on pagination and search criteria
export const getProjects = (page, perSize, search) => {
  return api.post(
    "/api/AdminProject/projects/dashboard-search",
    projectSearchValuesPayload(page, perSize, search),
  );
};

// get projects attributes
export const getProjectsAttributes = async (projectIds) => {
  if (projectIds.length === 0) return [];
  const { data } = await api.post(`/api/AdminProject/project-attributes`, {
    projectIds: projectIds,
  });

  return data?.map((option) => ({
    value: option.id,
    label: option.title,
  }));
};

// Delete a project after confirming it has no active recurring orders
export const deleteProject = async (id) => {
  try {
    const { data: hasActiveOrders } = await api.get(
      `/api/AdminProject/projects/${id}/has-active-recurring-orders`,
    );
    const { isConfirmed } = await swal(
      hasActiveOrders && "هذا المشروع لديه عمليات دفع",
      hasActiveOrders && "الرجاء تأكيد عملية الحذف",
      null,
    );

    if (isConfirmed) {
      await api.delete(`/api/AdminProject/projects/${id}`);
      await success("تم حذف المشروع بنجاح");
      return true;
    }
  } catch (error) {}
};

// Fetch all categories
export const getCategories = () => api.get("/api/AdminClassifications");

// Fetch all gift projects
export const getAllGiftProject = () =>
  api.get(`/api/adminGiftProject/gift-projects/short`);

// Fetch all banks
export const getBanks = () => api.get(`/api/AdminBank?Page=${1}&PageSize=`);

// Fetch SMS balance
export const getSmsBalance = () => api.get("/api/AdminSms/sms-balance");

// Send SMS with provided data
export const sendSMS = (data) => api.post("/api/AdminSms/sendSms", data);

// Fetch notifications based on pagination and search criteria
export const getNotifications = (page = 1, pageSize = "", search) => {
  return api.post(`/api/AdminNotifications/search`, {
    page: page,
    pageSize: pageSize,
    searchKey: search?.searchKey,
    toDate: datesToISO(search?.toDate) || null,
    fromDate: datesToISO(search?.fromDate) || null,
  });
};

// Import data from a given URL with provided data
const importData = (url, data) => {
  return api.post(
    `${url}`,
    {
      File: data.file,
    },
    formHeaders,
  );
};

// Save imported data based on the import target
export const saveImportData = async (data) => {
  if (data.importTo === "order") {
    return api.post(
      "/api/AdminOrder/import-orders-from-excel",
      {
        Order: data.file,
        OrderDetails: data.orderDetailsFile,
      },
      formHeaders,
    );
  } else {
    return importData(
      importDataUrlMapOptions[data.importTo][data.importFrom],
      data,
    );
  }
};

// add log export
export const addLogExport = (action, description) => {
  if (action === null) {
    return;
  }
  const payload = {
    action: parseInt(action),
    description: description,
  };
  return api.post("/api/Log/log-export", payload);
};

// get charity configs
export const getCharityConfigs = () => api.get("/api/Tenant");

// get user permissions — uses native fetch to skip the NProgress bar so
// the background permission refresh stays invisible to the user.
export const getUserPermissions = async () => {
  const { user, setUser } = useAuthStore.getState();

  const response = await fetch(`${baseURL}/api/AdminPermission/${user.id}`, {
    headers: { Authorization: `Bearer ${user.token}` },
  });

  const status = response.status;
  if (status === 401 || status === 403 || status === 419 || status === 503) {
    setUser(null);
    window.location.replace("/");
    return { data: null };
  }

  if (!response.ok) {
    throw new Error(`getUserPermissions failed: ${status}`);
  }

  const data = await response.json();
  return { data };
};

// Fetch enabled verification projects
export const getEnabledVerificationProjects = async (classificationId) => {
  const url = classificationId
    ? `/api/AdminVerification/enabled-verification-projects?classificationId=${classificationId}`
    : "/api/AdminVerification/enabled-verification-projects";

  const { data } = await api.get(url);
  return (
    data.projects?.map((option) => ({
      value: option.projectId,
      label: option.projectName,
    })) || []
  );
};

export const updateVerificationStagesSmsSendingDate = (data) =>
  api.put(
    "/api/AdminVerification/update-verification-stages-sms-sending-date",
    data,
  );
